import { useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Badge } from "@learn-me/ui";
import { GripVertical, Edit, Loader2, Trash2 } from "lucide-react";

interface Chapter {
  id: string;
  title: string;
  position: number;
  isPublished: boolean;
}

interface SortableChapterItemProps {
  chapter: Chapter;
  index: number;
  courseId: string;
  onDelete: (chapter: Chapter) => void;
  isDeleting: boolean;
}

function SortableChapterItem({
  chapter,
  index,
  courseId,
  onDelete,
  isDeleting,
}: SortableChapterItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: chapter.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-2 border rounded-lg bg-white ${
        isDragging ? "shadow-lg ring-2 ring-primary" : ""
      }`}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing touch-none"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </button>
      <span className="flex-1 text-sm truncate">
        {index + 1}. {chapter.title}
      </span>
      {!chapter.isPublished && (
        <Badge variant="outline" className="text-xs">
          Borrador
        </Badge>
      )}
      <Link to={`/admin/courses/${courseId}/chapters/${chapter.id}/edit`}>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <Edit className="h-3 w-3" />
        </Button>
      </Link>
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 text-red-600 hover:bg-red-50 hover:text-red-700"
        disabled={isDeleting}
        onClick={() => onDelete(chapter)}
        aria-label={`Eliminar capítulo ${chapter.title}`}
      >
        {isDeleting ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Trash2 className="h-3 w-3" />
        )}
      </Button>
    </div>
  );
}

interface SortableChapterListProps {
  chapters: Chapter[];
  courseId: string;
  onReorder: (
    chapters: Array<{ id: string; position: number }>,
  ) => Promise<void>;
  isReordering?: boolean;
  onDelete: (chapter: Chapter) => void;
  deletingChapterId?: string | null;
}

export function SortableChapterList({
  chapters,
  courseId,
  onReorder,
  isReordering = false,
  onDelete,
  deletingChapterId = null,
}: SortableChapterListProps) {
  // Sort chapters by position - derived from props (single source of truth)
  const sortedChapters = useMemo(
    () => [...chapters].sort((a, b) => a.position - b.position),
    [chapters],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = sortedChapters.findIndex(
        (item) => item.id === active.id,
      );
      const newIndex = sortedChapters.findIndex((item) => item.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      // Create the new order with updated positions
      const reorderedItems = arrayMove(sortedChapters, oldIndex, newIndex);
      const reorderPayload = reorderedItems.map((item, idx) => ({
        id: item.id,
        position: idx + 1,
      }));

      // Call the parent handler (which handles optimistic updates)
      await onReorder(reorderPayload);
    },
    [sortedChapters, onReorder],
  );

  const itemIds = useMemo(
    () => sortedChapters.map((item) => item.id),
    [sortedChapters],
  );

  return (
    <div className="relative">
      {isReordering && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10 rounded-lg">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {sortedChapters.map((chapter, index) => (
              <SortableChapterItem
                key={chapter.id}
                chapter={chapter}
                index={index}
                courseId={courseId}
                onDelete={onDelete}
                isDeleting={deletingChapterId === chapter.id}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
